// src/components/ui/ZeloScore.jsx
//
// The live ZeloScore readout for community_score competitions.
//
// This is a READ-ONLY display element — it is the heartbeat of a submission
// card. It inherits the visual energy the vote button carries on voting cards:
// a glowing pill that pulses when the score changes, pulling the eye toward
// "something is happening here, open me."
//
// The score itself is deliberately opaque — users see the number move but are
// never told the formula. Curiosity is the engagement driver.

import React, { useEffect, useRef, useState } from "react";

const ZeloScore = ({ score = 0, size = "card" }) => {
  const [pulse, setPulse] = useState(false);
  const prevScore = useRef(score);

  // Pulse animation whenever the score changes (e.g. after a poll update)
  useEffect(() => {
    if (score !== prevScore.current) {
      setPulse(true);
      prevScore.current = score;
      const t = setTimeout(() => setPulse(false), 600);
      return () => clearTimeout(t);
    }
  }, [score]);

  const displayScore = Number(score ?? 0).toFixed(1);

  // Two sizings: compact pill for the grid card, larger for the modal
  const sizing =
    size === "modal"
      ? "text-2xl px-4 py-2 gap-2"
      : "text-sm px-3 py-1.5 gap-1.5";

  const logoSize = size === "modal" ? "w-6 h-6" : "w-4 h-4";

  return (
    <div className="relative leading-none select-none" aria-label={`ZeloScore ${displayScore}`}>
      {/* Glow halo — mirrors the vote button's glow language */}
      <div
        className={`pointer-events-none absolute inset-0 rounded-full blur-xl transition-opacity duration-300
          ${pulse ? "opacity-70 bg-mikado-yellow/40" : "opacity-30 bg-mikado-yellow/20"}`}
      />

      {/* Pill */}
      <div
        className={`relative z-10 inline-flex items-center rounded-full
          bg-stone-brown/80 backdrop-blur-sm border border-mikado-yellow/30
          font-poppins font-bold text-mikado-yellow
          transition-transform duration-300 ease-out
          ${sizing}
          ${pulse ? "scale-110" : "scale-100"}`}
      >
        {/* Zelospace logo mark — using the brand 'Z' as a glyph.
            Swap for an <img> of the actual logo asset when available. */}
        <span
          className={`inline-flex items-center justify-center rounded-full
            bg-mikado-yellow text-stone-brown font-extrabold ${logoSize}`}
          style={{ fontSize: size === "modal" ? "0.85rem" : "0.6rem" }}
        >
          Z
        </span>
        <span className="tabular-nums">{displayScore}</span>
      </div>
    </div>
  );
};

export default ZeloScore;